import { useState } from "react";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Button } from "@/components/ui/button";
import { Note } from "@/components/ui/Note";
import { Panel } from "@/components/ui/Panel";
import type { ReactNode } from "react";
import { Plus, Trash2 } from "lucide-react";
import type { PointModel } from "@patent-studio/api-types";
import { cn } from "@/lib/utils";
import type { Definitions } from "@/lib/element-registry";
import type { LineKind, NumeralStyle } from "@/lib/element-registry";
import { AnnotatedDrawing } from "./AnnotatedDrawing";
import { ADD_BUTTON } from "./panel-styles";
import { ReuseMenu } from "./ReuseMenu";
import type { NamedFigure } from "./TitleOffers";
import { AnnotationToolbar } from "./AnnotationToolbar";
import { useEscapeScope } from "@/components/ui/escape-scope";
import type { Placement } from "./AnnotatedDrawing";
import { useFigureZoom } from "./figure-zoom";

/**
 * Every way one mark can be changed, gathered rather than passed one prop at a time.
 *
 * They travel together because they are one idea, a change to the mark that is selected,
 * and because each surface between the store and the controls would otherwise repeat six
 * near-identical lines to hand them along.
 */
export interface MarkEdits {
  line: (id: string, kind: LineKind) => void;
  arrowhead: (id: string, on: boolean) => void;
  movePin: (id: string, pin: PointModel) => void;
  aimTip: (id: string, tip: PointModel) => void;
  bend: (id: string, index: number, control: PointModel) => void;
  numeralStyle: (id: string, style: NumeralStyle) => void;
}

/**
 * A placement in the making, one state per click still owed.
 *
 * Ratified as a three-state machine: idle, awaiting the pin, awaiting the tip. Idle is the
 * absent value, so the machine cannot express a fourth state, and nothing is written until
 * the last transition hands both points over together. An undefined element id means a new
 * element; which id that turns out to be is left to the write, so a placement started
 * before somebody else's landed does not hold an id that has since been taken.
 */
type Placing =
  | { step: "awaiting-pin"; elementId: string | undefined }
  | { step: "awaiting-tip"; elementId: string | undefined; pin: PointModel };

/** Where a numeral comes from: minted here, or one the set already holds. */
function AddRow({
  definitions,
  assigned,
  fileId,
  figures,
  onStart,
  minting,
  onCancel,
}: {
  definitions: Definitions;
  assigned: Map<string, string>;
  fileId: string;
  figures: NamedFigure[];
  onStart: (elementId: string | undefined) => void;
  /** Whether a new numeral is being placed: the toggle shows it is on, and a press turns it off. */
  minting: boolean;
  onCancel: () => void;
}) {
  return (
    <>
      <MicroLabel>Add</MicroLabel>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-pressed={minting}
          onClick={() => {
            if (minting) onCancel();
            else onStart(undefined);
          }}
          className={cn(
            ADD_BUTTON,
            "border-transparent bg-accent text-accent-foreground",
            minting && "ring-2 ring-accent ring-offset-2 ring-offset-muted",
          )}
        >
          <Plus className="size-3.5" aria-hidden />
          New numeral
        </button>
        <ReuseMenu
          definitions={definitions}
          assigned={assigned}
          fileId={fileId}
          figures={figures}
          onChoose={onStart}
        />
      </div>
    </>
  );
}

/**
 * Taking a mark off the drawing, on a row of its own.
 *
 * Its own row because it is the one control here that destroys something. Under the caption
 * that says Add it read as another way to put a numeral down.
 */
function RemoveRow({
  selected,
  onRemove,
}: {
  selected: Placement | undefined;
  onRemove: () => void;
}) {
  return (
    <>
      <MicroLabel>Remove</MicroLabel>
      <div className="flex items-center gap-2">
        <Button
          variant="danger"
          size="dense"
          type="button"
          onClick={onRemove}
          disabled={selected === undefined}
        >
          <Trash2 aria-hidden />
          Remove numeral
        </Button>
      </div>
    </>
  );
}

/**
 * The block that starts a placement, and the one control that ends one already made.
 *
 * Every way in ends in the same place, a placement waiting for its two clicks, which is why
 * they sit together rather than beside the controls that act on a pin already drawn.
 *
 * A placement under way is said by the drawing rather than here: the pointer turns to a
 * crosshair over it, and Escape calls the whole thing off. A row appearing mid-placement to
 * narrate the clicks pushed the drawing down the screen at the moment it was being aimed at.
 */
function PlacementPanel({
  definitions,
  assigned,
  fileId,
  figures,
  onStart,
  onRemove,
  selected,
  step,
  minting,
  onCancel,
}: {
  definitions: Definitions;
  assigned: Map<string, string>;
  fileId: string;
  figures: NamedFigure[];
  onStart: (elementId: string | undefined) => void;
  onRemove: () => void;
  selected: Placement | undefined;
  /** Which click a numeral being placed waits for, if one is. */
  step: Placing["step"] | undefined;
  minting: boolean;
  onCancel: () => void;
}) {
  return (
    <Panel
      shape="row"
      className="mb-2.5 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-[9px] bg-muted"
    >
      <AddRow
        definitions={definitions}
        assigned={assigned}
        fileId={fileId}
        figures={figures}
        onStart={onStart}
        minting={minting}
        onCancel={onCancel}
      />
      <RemoveRow selected={selected} onRemove={onRemove} />
      {/* The two clicks are different things and the crosshair cannot say which comes
          first, so while a numeral is placed one line says what the next click does. The
          region stays, empty, so a reader hears the first line when it arrives. */}
      <div
        role="status"
        aria-label="Placing a numeral"
        className="col-span-2 empty:hidden"
      >
        {step !== undefined && <Note hint>{PLACING_HINT[step]}</Note>}
      </div>
    </Panel>
  );
}

/** What the next click on the drawing does, for each step of placing a numeral. */
const PLACING_HINT: Record<Placing["step"], string> = {
  "awaiting-pin":
    "Click where the numeral goes, then click the part it points to.",
  "awaiting-tip": "Now click the part the numeral points to.",
};

/**
 * The placement machine: what a click on the drawing means, and what closes it.
 *
 * Held apart from the card because a half-made placement is a sequence rather than a
 * setting, and every rule about it is a rule about the click that comes next. Nothing
 * reaches the store until the transition that closes the machine, which is the one place
 * both points exist together.
 */
function usePlacement(
  onPlace: (
    pin: PointModel,
    tip: PointModel,
    elementId: string | undefined,
  ) => void,
) {
  const [placing, setPlacing] = useState<Placing | undefined>(undefined);
  const cancel = () => {
    setPlacing(undefined);
  };
  useEscapeScope("gesture", placing !== undefined, cancel);

  return {
    placing,
    cancel,
    start: (elementId: string | undefined) => {
      setPlacing({ step: "awaiting-pin", elementId });
    },
    clicked: (point: PointModel) => {
      if (placing === undefined) return;
      if (placing.step === "awaiting-pin") {
        setPlacing({
          step: "awaiting-tip",
          elementId: placing.elementId,
          pin: point,
        });
        return;
      }
      cancel();
      onPlace(placing.pin, point, placing.elementId);
    },
  };
}

/**
 * The drawing with the tools that annotate it.
 *
 * The placement machine lives here, between the panel that starts a placement and the
 * drawing whose clicks advance it. Each click arrives as a bare point, and this component
 * decides what it means from the step the machine is in, so a half-made placement can never
 * reach the store: the write happens only on the transition that closes the machine, with
 * both points in hand.
 */
interface AnnotationCardProps {
  thumbnailUrl: string;
  filename: string;
  definitions: Definitions;
  assigned: Map<string, string>;
  fileId: string;
  figures: NamedFigure[];
  selected: Placement | undefined;
  onSelect: (placement: Placement | undefined) => void;
  onPlace: (
    pin: PointModel,
    tip: PointModel,
    elementId: string | undefined,
  ) => void;
  onRemove: (placement: Placement) => void;
  edits: MarkEdits;
  /** Laid over the drawing, for whatever acts on the drawing without annotating it. */
  overlay?: ReactNode;
  /** Passed to the drawing's frame, which is where this is explained. */
  fills?: boolean;
}

export function AnnotationCard({
  thumbnailUrl,
  filename,
  definitions,
  assigned,
  fileId,
  figures,
  selected,
  onSelect,
  onPlace,
  onRemove,
  edits,
  overlay,
  fills = false,
}: AnnotationCardProps) {
  const { placing, start, clicked, cancel } = usePlacement(onPlace);
  // Held here because this is the nearest thing that owns both the toolbar the level is
  // read on and the drawing it is applied to, and because a fitted frame is the only kind
  // with a fitted size to multiply: a drawing on a scrolling page has nothing to zoom.
  const zoom = useFigureZoom(fills);
  const here = definitions.figures.find((figure) => figure.file_id === fileId);
  // Looked up by id on every render rather than carried on the selection, so the controls
  // read the mark as the set now describes it. A queued change can take its line away or
  // curve it underneath the attorney, and a toolbar drawn from a snapshot would go on
  // offering what the mark no longer holds.
  const chosen = here?.instances?.find((mark) => mark.id === selected?.id);

  return (
    <Panel
      as="section"
      className={cn("shadow-sm", fills && "flex h-full min-h-0 flex-col")}
    >
      <PlacementPanel
        definitions={definitions}
        assigned={assigned}
        fileId={fileId}
        figures={figures}
        selected={selected}
        step={placing?.step}
        minting={placing !== undefined && placing.elementId === undefined}
        onCancel={cancel}
        onStart={start}
        onRemove={() => {
          if (selected !== undefined) onRemove(selected);
        }}
      />

      <AnnotationToolbar
        instance={chosen}
        settings={definitions.settings}
        edits={edits}
        zoom={fills ? zoom : undefined}
      />

      <AnnotatedDrawing
        thumbnailUrl={thumbnailUrl}
        filename={filename}
        instances={here?.instances ?? []}
        definitions={definitions}
        assigned={assigned}
        // Read from the set rather than passed in, because the conventions belong to the
        // whole set and the card already holds them.
        settings={definitions.settings}
        selected={selected}
        placing={placing !== undefined}
        pendingPin={placing?.step === "awaiting-tip" ? placing.pin : undefined}
        onSelect={onSelect}
        onPlacePoint={clicked}
        onMovePin={edits.movePin}
        onAimTip={edits.aimTip}
        onBend={edits.bend}
        overlay={overlay}
        fills={fills}
        zoom={zoom.level}
        onWhole={zoom.fitWhole}
      />
    </Panel>
  );
}
